import { METALS, PIECES, weightOf, imgKey, IMG_SIZES, THUMB } from './data.js';

/* global __MEDIA__ */
const MEDIA = __MEDIA__; // { key: hashed URL }, injected by the build
const $ = (s, r) => (r || document).querySelector(s);
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

/* =========================================================
   Hero film. Starts after first interaction (or a few seconds
   after load) so it never competes with the first paint.
   ========================================================= */
(function film() {
  const SHOTS = [
    { id: 4941455, cap: 'At the mill' },
    { id: 4941355, cap: 'Facing the bar stock' },
    { id: 4941464, cap: 'A squared blank, before the faces are cut' }
  ];
  const SHOT_LEN = 7;
  const vids = [...document.querySelectorAll('#film video')];
  const caption = $('#shotCaption');
  const tickEls = [...document.querySelectorAll('#shotTicks i')];
  const toggle = $('#filmToggle');
  let idx = 0, active = 0, started = false, userPaused = reduceMotion, inView = true, switching = false;

  const src = (id) => {
    const q = innerWidth < 800 ? 'sd_960_540_25fps' : 'hd_1280_720_50fps';
    return `https://videos.pexels.com/video-files/${id}/${id}-${q}.mp4`;
  };
  const load = (v, i) => { v.src = src(SHOTS[i].id); v.load(); };

  function setTicks() {
    tickEls.forEach((t, i) => { t.style.transform = i < idx ? 'none' : 'scaleX(0)'; });
  }

  function advance() {
    if (switching) return;
    switching = true;
    const next = 1 - active, nIdx = (idx + 1) % SHOTS.length, nv = vids[next];
    const go = () => {
      nv.currentTime = 0;
      nv.play().catch(() => {});
      vids[active].classList.remove('on');
      nv.classList.add('on');
      const old = vids[active];
      active = next; idx = nIdx;
      caption.style.opacity = 0;
      setTimeout(() => { caption.textContent = SHOTS[idx].cap; caption.style.opacity = 1; }, 400);
      setTicks();
      setTimeout(() => { old.pause(); load(old, (idx + 1) % SHOTS.length); switching = false; }, 1500);
    };
    if (nv.readyState >= 2) go(); else nv.addEventListener('canplay', go, { once: true });
  }

  function tick() {
    const v = vids[active];
    if (v.duration) {
      const len = Math.min(SHOT_LEN, v.duration - 0.4);
      tickEls[idx].style.transform = `scaleX(${Math.min(1, v.currentTime / len)})`;
      if (!userPaused && v.currentTime >= len) advance();
    }
    requestAnimationFrame(tick);
  }

  function sync() {
    toggle.textContent = userPaused ? 'Play film' : 'Pause film';
    toggle.setAttribute('aria-pressed', String(userPaused));
    if (!started) return;
    const v = vids[active];
    if (!userPaused && inView && !document.hidden) v.play().catch(() => {}); else v.pause();
  }

  function start() {
    if (started) return;
    started = true;
    TRIGGERS.forEach((t) => removeEventListener(t, start));
    load(vids[0], 0);
    load(vids[1], 1);
    vids[0].addEventListener('playing', () => vids[0].classList.add('on'), { once: true });
    sync();
    requestAnimationFrame(tick);
  }

  const TRIGGERS = ['pointermove', 'pointerdown', 'scroll', 'keydown', 'touchstart'];
  TRIGGERS.forEach((t) => addEventListener(t, start, { passive: true, once: true }));
  addEventListener('load', () => setTimeout(start, 4500));

  toggle.addEventListener('click', () => { userPaused = !userPaused; start(); sync(); });
  document.addEventListener('visibilitychange', sync);
  new IntersectionObserver(([e]) => { inView = e.isIntersecting; sync(); }).observe($('.hero'));
  vids.forEach((v) => v.addEventListener('ended', () => { if (!userPaused) advance(); }));
  setTicks();
  sync();
})();

/* =========================================================
   Pieces: metal picker, quantity, lazy 3D viewer
   ========================================================= */
const state = {};
const viewers = {};
let viewerModule = null;
const loadViewer = () => (viewerModule ||= import('./viewer.js'));

function setPoster(img, pid, metal) {
  img.src = MEDIA[imgKey(pid, metal, IMG_SIZES[1])];
  img.srcset = IMG_SIZES.map((s) => `${MEDIA[imgKey(pid, metal, s)]} ${s}w`).join(', ');
  img.alt = `${PIECES[pid].name} in ${METALS[metal].name.toLowerCase()}`;
}

const io3d = new IntersectionObserver((entries) => {
  entries.forEach((e) => {
    if (!e.isIntersecting) return;
    io3d.unobserve(e.target);
    const pid = e.target.dataset.pid;
    loadViewer().then(({ mount }) => mount(e.target, pid, state[pid].metal, () => e.target.classList.add('live')))
      .then((v) => { viewers[pid] = v; if (v.metal !== state[pid].metal) v.setMetal(state[pid].metal); })
      .catch(() => { /* keep the still image if WebGL is unavailable */ });
  });
}, { rootMargin: '300px 0px' });

document.querySelectorAll('.piece').forEach((el) => {
  const pid = el.dataset.pid;
  state[pid] = { metal: PIECES[pid].metal, qty: 1 };
  const vitrine = $('.vitrine', el);
  const poster = $('.poster', el);
  io3d.observe(vitrine);

  el.querySelectorAll('.chip input').forEach((inp) => inp.addEventListener('change', () => {
    const m = inp.value;
    state[pid].metal = m;
    $('.metal-name', el).textContent = METALS[m].name;
    $('.tb-metal', el).textContent = METALS[m].name;
    $('.w', el).textContent = weightOf(pid, m);
    setPoster(poster, pid, m);
    if (viewers[pid]) viewers[pid].setMetal(m);
  }));
  el.querySelectorAll('.qty button').forEach((b) => b.addEventListener('click', () => {
    state[pid].qty = Math.max(1, Math.min(12, state[pid].qty + Number(b.dataset.q)));
    $('.qty output', el).textContent = state[pid].qty;
  }));
  const msg = $('.added-msg', el);
  $('.add', el).addEventListener('click', () => {
    const { metal, qty } = state[pid];
    addToCart(pid, metal, qty);
    msg.innerHTML = `Added ${qty} in ${METALS[metal].name.toLowerCase()}. <button type="button">View request</button>`;
    $('button', msg).addEventListener('click', openDrawer);
  });
});

/* =========================================================
   Request (cart)
   ========================================================= */
const KEY = 'luxurydice.request';
let cart = [];
try {
  const saved = JSON.parse(localStorage.getItem(KEY) || '[]');
  if (Array.isArray(saved)) cart = saved.filter((it) => PIECES[it.pid] && METALS[it.metal] && it.qty > 0);
} catch (_) { /* storage unavailable */ }
const save = () => { try { localStorage.setItem(KEY, JSON.stringify(cart)); } catch (_) { /* storage unavailable */ } };

function addToCart(pid, metal, qty) {
  const ex = cart.find((it) => it.pid === pid && it.metal === metal);
  if (ex) ex.qty = Math.min(24, ex.qty + qty); else cart.push({ pid, metal, qty });
  save(); renderCart();
  const c = $('#cartCount'); c.classList.add('bump'); setTimeout(() => c.classList.remove('bump'), 300);
}

const lineText = (it) => `${PIECES[it.pid].name}, ${PIECES[it.pid].kind}, in ${METALS[it.metal].name} (${METALS[it.metal].grade}) × ${it.qty}`;

function renderCart() {
  const total = cart.reduce((s, it) => s + it.qty, 0);
  $('#cartCount').textContent = total;
  $('#cartCountLabel').textContent = total === 1 ? 'piece' : 'pieces';
  $('#empty').hidden = cart.length > 0;
  $('#listFoot').hidden = cart.length === 0;
  const ul = $('#lines');
  ul.innerHTML = '';
  cart.forEach((it, i) => {
    const P = PIECES[it.pid], M = METALS[it.metal];
    const li = document.createElement('li');
    li.className = 'line';
    li.innerHTML = `
      <img class="thumb" alt="" width="${THUMB}" height="${THUMB}" src="${MEDIA[imgKey(it.pid, it.metal, THUMB)]}">
      <div>
        <h3>${esc(P.name)}</h3>
        <p class="meta">${esc(P.type)} in ${M.name.toLowerCase()}</p>
        <div class="qty" role="group" aria-label="Quantity of ${esc(P.name)}"><button type="button" data-q="-1" aria-label="One fewer">−</button><output>${it.qty}</output><button type="button" data-q="1" aria-label="One more">+</button></div>
      </div>
      <button type="button" class="remove">Remove</button>`;
    ul.appendChild(li);
    li.querySelectorAll('.qty button').forEach((b) => b.addEventListener('click', () => {
      it.qty += Number(b.dataset.q);
      if (it.qty <= 0) cart.splice(i, 1); else it.qty = Math.min(24, it.qty);
      save(); renderCart();
    }));
    $('.remove', li).addEventListener('click', () => { cart.splice(i, 1); save(); renderCart(); });
  });
}

/* ---------- drawer ---------- */
const drawer = $('#drawer'), scrim = $('#scrim');
let lastFocus = null;

function showView(name) {
  ['List', 'Form', 'Done'].forEach((v) => { $('#view' + v).hidden = v !== name; });
  $('#drawerTitle').textContent = name === 'Done' ? 'Thank you' : 'Your request';
}
function openDrawer() {
  lastFocus = document.activeElement;
  showView('List'); renderCart();
  scrim.hidden = false;
  drawer.inert = false;
  drawer.classList.add('ready');
  requestAnimationFrame(() => { scrim.classList.add('open'); drawer.classList.add('open'); });
  document.body.classList.add('locked');
  setTimeout(() => $('#closeCart').focus(), 60);
}
function closeDrawer() {
  scrim.classList.remove('open'); drawer.classList.remove('open');
  drawer.inert = true;
  document.body.classList.remove('locked');
  setTimeout(() => { scrim.hidden = true; drawer.classList.remove('ready'); }, 450);
  if (lastFocus && lastFocus.focus) lastFocus.focus();
}

$('#openCart').addEventListener('click', openDrawer);
$('#openCart2').addEventListener('click', openDrawer);
$('#closeCart').addEventListener('click', closeDrawer);
$('#doneClose').addEventListener('click', closeDrawer);
$('#emptyBrowse').addEventListener('click', closeDrawer);
scrim.addEventListener('click', closeDrawer);
document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && drawer.classList.contains('open')) closeDrawer(); });

$('#toForm').addEventListener('click', () => {
  if (!cart.length) return;
  $('#summary').innerHTML = cart.map((it) => `<div><span>${esc(PIECES[it.pid].name)} in ${METALS[it.metal].name.toLowerCase()}</span><b>× ${it.qty}</b></div>`).join('');
  $('#formError').hidden = true;
  showView('Form');
  setTimeout(() => $('#emailField').focus(), 60);
});
$('#backToList').addEventListener('click', () => showView('List'));

/* ---------- Netlify form submission ---------- */
const form = $('#requestForm');
const email = $('#emailField');
const err = $('#formError');
const showErr = (t) => { err.textContent = t; err.hidden = false; };

email.addEventListener('input', () => { email.removeAttribute('aria-invalid'); err.hidden = true; });

form.addEventListener('submit', (e) => {
  e.preventDefault();
  err.hidden = true;
  if (!email.value.trim()) { email.setAttribute('aria-invalid', 'true'); showErr('Enter your email address so we can reply.'); email.focus(); return; }
  if (!email.validity.valid) { email.setAttribute('aria-invalid', 'true'); showErr('Enter an email address in the form name@example.com.'); email.focus(); return; }
  if (!cart.length) { showErr('Your request is empty. Add at least one piece first.'); return; }
  $('#selectionField').value = cart.map(lineText).join('\n');

  const btn = $('#submitBtn');
  btn.disabled = true; btn.textContent = 'Sending…';
  fetch('/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(new FormData(form)).toString()
  }).then((res) => {
    if (!res.ok) throw new Error('HTTP ' + res.status);
    cart = []; save(); renderCart(); form.reset();
    showView('Done');
  }).catch(() => {
    showErr('The request did not go through. Check your connection and select Send request again.');
  }).finally(() => { btn.disabled = false; btn.textContent = 'Send request'; });
});

/* =========================================================
   Page chrome
   ========================================================= */
const nav = $('#nav');
const hero = $('.hero');
let heroH = hero.offsetHeight;
addEventListener('resize', () => { heroH = hero.offsetHeight; }, { passive: true });
const onScroll = () => nav.classList.toggle('solid', scrollY > heroH - 80);
addEventListener('scroll', onScroll, { passive: true });
onScroll();
renderCart();
